import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "../../helpers";
import {
  PageWrapper,
  PageHead,
  PageHeadContent,
  Eyebrow,
  PageTitle,
  PageDescription,
  ServiceIndex,
  IndexLink,
  IndexLabel,
  IndexTitle,
  IndexSub,
  ServiceGroup,
  GroupInner,
  GroupIntro,
  GroupNum,
  GroupTitle,
  GroupIntroText,
  GroupCount,
  ServiceList,
  ServiceItem,
  ServiceTitle,
  ServiceDescription,
  Arrow,
  StartSection,
  StartContent,
  StartTitle,
  StartList,
  StartListItem,
  StartNum,
  ClosingSection,
  ClosingWrap,
  ClosingTitle,
  CTAButton,
  CTAArrow,
} from "./services-new.styles";

export const ServicesNew = () => {
  const navigate = useNavigate();

  const navigateAndTrack = (path: string, event: string) => {
    navigate(path);
    trackEvent(event);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <PageWrapper>
      <main>
        {/* Page Header */}
        <PageHead>
          <PageHeadContent>
            <Eyebrow>Services</Eyebrow>
            <PageTitle>Build it. Fix it. <em>Lead it.</em></PageTitle>
          </PageHeadContent>
          <PageDescription>
            Fourteen ways I help founders and teams, grouped by what you need. Not sure which one fits? That is what the discovery call is for.
          </PageDescription>
        </PageHead>

        {/* Service Group Index */}
        <nav aria-label="Service groups">
          <ServiceIndex>
            <IndexLink onClick={() => scrollToSection('build')}>
              <IndexLabel>01</IndexLabel>
              <IndexTitle>Build</IndexTitle>
              <IndexSub>MVPs, apps, architecture</IndexSub>
            </IndexLink>
            <IndexLink onClick={() => scrollToSection('ai-data')}>
              <IndexLabel>02</IndexLabel>
              <IndexTitle>AI &amp; data</IndexTitle>
              <IndexSub>AI systems, automation, marketing</IndexSub>
            </IndexLink>
            <IndexLink onClick={() => scrollToSection('audit')}>
              <IndexLabel>03</IndexLabel>
              <IndexTitle>Audit</IndexTitle>
              <IndexSub>Due diligence, code, SEO, accessibility</IndexSub>
            </IndexLink>
            <IndexLink onClick={() => scrollToSection('lead')}>
              <IndexLabel>04</IndexLabel>
              <IndexTitle>Lead</IndexTitle>
              <IndexSub>Technical leadership, velocity</IndexSub>
            </IndexLink>
          </ServiceIndex>
        </nav>

        {/* 01 BUILD */}
        <ServiceGroup id="build" aria-labelledby="build-title">
          <GroupInner>
            <GroupIntro>
              <GroupNum aria-hidden="true">01</GroupNum>
              <GroupTitle id="build-title">Build</GroupTitle>
              <GroupIntroText>From first prototype to a platform built to scale.</GroupIntroText>
              <GroupCount>4 services</GroupCount>
            </GroupIntro>
            <ServiceList>
              <ServiceItem>
                <ServiceTitle>Rapid Prototyping</ServiceTitle>
                <ServiceDescription>Fast-track your product vision with MVP development, proof-of-concept builds, and iterative prototyping that validates ideas quickly.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
              <ServiceItem>
                <ServiceTitle>Mobile App Development</ServiceTitle>
                <ServiceDescription>Native iOS and Android applications, cross-platform solutions with React Native, and progressive web apps that deliver exceptional mobile experiences.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
              <ServiceItem>
                <ServiceTitle>Architecture Design</ServiceTitle>
                <ServiceDescription>Scalable system architecture designed to grow with your business and handle increasing demands.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
              <ServiceItem>
                <ServiceTitle>Infrastructure &amp; Data</ServiceTitle>
                <ServiceDescription>CI/CD pipelines, data infrastructure, automated testing, and deployment workflows that scale with your growth.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
            </ServiceList>
          </GroupInner>
        </ServiceGroup>

        {/* 02 AI & DATA (dark) */}
        <ServiceGroup className="dark" id="ai-data" aria-labelledby="ai-title">
          <GroupInner>
            <GroupIntro>
              <GroupNum aria-hidden="true">02</GroupNum>
              <GroupTitle id="ai-title">AI &amp; data</GroupTitle>
              <GroupIntroText>AI that works in production, automation that saves you hours, and data you can act on.</GroupIntroText>
              <GroupCount>4 services</GroupCount>
            </GroupIntro>
            <ServiceList>
              <ServiceItem>
                <ServiceTitle>AI Systems &amp; Integration</ServiceTitle>
                <ServiceDescription>End-to-end AI implementation: model selection, prompt engineering, API integration, and production-ready AI pipelines.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
              <ServiceItem>
                <ServiceTitle>Workflow Automation</ServiceTitle>
                <ServiceDescription>AI-powered automation for repetitive tasks: scheduled social media content, lead outreach sequences, customer response handling, and cross-platform integrations that run while you sleep.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
              <ServiceItem>
                <ServiceTitle>AI Movie Marketing</ServiceTitle>
                <ServiceDescription>Viral short-form video content using AI tools for film promotion, trailers, and social campaigns. See examples of my work on <a href="https://instagram.com/reneelouisetech" target="_blank" rel="noopener noreferrer" style={{ color: '#D9442B', textDecoration: 'underline' }}>Instagram</a>.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
              <ServiceItem>
                <ServiceTitle>Analytics &amp; Reporting</ServiceTitle>
                <ServiceDescription>Custom dashboard creation, data visualization, and reporting systems that transform raw data into actionable business insights.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
            </ServiceList>
          </GroupInner>
        </ServiceGroup>

        {/* 03 AUDIT */}
        <ServiceGroup id="audit" aria-labelledby="audit-title">
          <GroupInner>
            <GroupIntro>
              <GroupNum aria-hidden="true">03</GroupNum>
              <GroupTitle id="audit-title">Audit</GroupTitle>
              <GroupIntroText>An honest, expert view of what you have, before it costs you.</GroupIntroText>
              <GroupCount>4 services</GroupCount>
            </GroupIntro>
            <ServiceList>
              <ServiceItem>
                <ServiceTitle>Technical Due Diligence</ServiceTitle>
                <ServiceDescription>Deep-dive audits to identify technical debt, security gaps, and scalability issues before they become problems.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
              <ServiceItem>
                <ServiceTitle>Code Reviews &amp; Audits</ServiceTitle>
                <ServiceDescription>Objective code reviews that ensure quality, maintainability, and best practices across your codebase.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
              <ServiceItem>
                <ServiceTitle>SEO &amp; Performance</ServiceTitle>
                <ServiceDescription>Technical SEO audits, performance optimization, and search engine visibility improvements to drive organic growth.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
              <ServiceItem>
                <ServiceTitle>Accessibility Audit</ServiceTitle>
                <ServiceDescription>Comprehensive WCAG compliance audits to ensure your product is accessible to all users and meets legal requirements.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
            </ServiceList>
          </GroupInner>
        </ServiceGroup>

        {/* 04 LEAD */}
        <ServiceGroup id="lead" aria-labelledby="lead-title">
          <GroupInner>
            <GroupIntro>
              <GroupNum aria-hidden="true">04</GroupNum>
              <GroupTitle id="lead-title">Lead</GroupTitle>
              <GroupIntroText>Senior technical direction without a full-time hire.</GroupIntroText>
              <GroupCount>2 services</GroupCount>
            </GroupIntro>
            <ServiceList>
              <ServiceItem>
                <ServiceTitle>Technical Leadership</ServiceTitle>
                <ServiceDescription>CTO services providing strategic oversight, vendor evaluation, and engineering vision for growing teams.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
              <ServiceItem>
                <ServiceTitle>Engineering Velocity</ServiceTitle>
                <ServiceDescription>Identify bottlenecks, streamline delivery pipelines, and implement practices that accelerate your team's output.</ServiceDescription>
                <Arrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></Arrow>
              </ServiceItem>
            </ServiceList>
          </GroupInner>
        </ServiceGroup>

        {/* HOW WE START */}
        <StartSection>
          <StartContent>
            <Eyebrow>How we start</Eyebrow>
            <StartTitle>One call to find <em>the right fit.</em></StartTitle>
          </StartContent>
          <StartList>
            <StartListItem>
              <StartNum>01</StartNum>
              <span>Discovery call: what you are building and where it is stuck.</span>
            </StartListItem>
            <StartListItem>
              <StartNum>02</StartNum>
              <span>A follow up call to walk through my recommendations, agree the right service and define the scope and priorities.</span>            </StartListItem>
            <StartListItem>
              <StartNum>03</StartNum>
              <span>Work begins, with a clear timeline provided and updates at every stage.</span>
            </StartListItem>
          </StartList>
        </StartSection>

        {/* CLOSING CTA */}
        <ClosingSection>
          <ClosingWrap>
            <ClosingTitle>Not sure where to start? <em>Let's talk.</em></ClosingTitle>
            <CTAButton onClick={() => navigateAndTrack("/contact", "Services - Book call click")}>
              Book a discovery call <CTAArrow aria-hidden="true"><ArrowRight size={18} color="#D9442B" /></CTAArrow>
            </CTAButton>
          </ClosingWrap>
        </ClosingSection>
      </main>
    </PageWrapper>
  );
};
